import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import produce from 'immer';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'sonner';
import {
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../models';
import { AI_CAMPAIGN_MINIMUM_LEAD_HOURS, earliestAICampaignSend } from '../../../models/AICampaign';
import { EmailComposerContext, IEmailComposerContext } from '../../../models/Email';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { useErrorMessages, useFullscreenModal } from '../../../models/hooks/appStateHooks';
import { Topics } from '../../../models/LocalNotificationTopics';
import { useEmailCampaignCancelScheduleMutation, useEmailUpdateSendScheduleMutation } from '../../../queries';
import { invalidateAllInfiniteCampaignReportQueries } from '../../../queries/Reports/useInfiniteCampaignReportsQuery';
import { CampaignsApprovalViewModel, ComposeEmailViewModel } from '../../../viewmodels/AppViewModels';
import { CampaignDetailsSummary } from '../../components/campaigns/CampaignDetailsSummary';
import { AICampaignQueuedView } from './AICampaignQueuedView';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../components/ConfirmationDialog';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../components/LocalNotificationObserver/WithNotificationService';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { PrivateRoute } from '../../components/PrivateRoute';
import { WarningIcon } from '../../components/svgs/icons/WarningIcon';
import { ViewCitationsModal } from '../../components/templates/ViewCitationsModal';
import { BulkEmailApprovalComposer } from '../bulkEmailApproval/BulkEmailApprovalComposer';
import { BulkEmailApprovalConfirmation } from '../bulkEmailApproval/BulkEmailApprovalConfirmation';
import { BulkEmailApprovalSetSchedule } from '../bulkEmailApproval/BulkEmailApprovalSetSchedule';
import { EditCampaign } from '../templates/EditCampaign';
import { EditSendOptions } from '../templates/EditSendOptions';
import { styleSheet } from './styles';

export function createSendTestEmailHandler(
	emailComposer: ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>
): (
	selectedUserId: string,
	content?: Api.IRawRichTextContentState,
	subject?: string
) => Promise<Api.IOperationResult<Api.ISendEmailResponse>> {
	return async (selectedUserId: string, content?: Api.IRawRichTextContentState, subject?: string) => {
		if (!emailComposer?.emailMessage?.sendTestEmail) {
			throw new Error('sendTestEmail method not available');
		}
		if (content !== undefined || subject !== undefined) {
			const originalContent = emailComposer.emailMessage.content;
			const originalSubject = emailComposer.emailMessage.subject;
			try {
				if (content !== undefined) emailComposer.emailMessage.content = content;
				if (subject !== undefined) emailComposer.emailMessage.subject = subject;
				return await emailComposer.emailMessage.sendTestEmail({
					to: { id: selectedUserId } as Api.ContactViewModel,
				});
			} finally {
				emailComposer.emailMessage.content = originalContent;
				emailComposer.emailMessage.subject = originalSubject;
			}
		}
		return emailComposer.emailMessage.sendTestEmail({
			to: { id: selectedUserId } as Api.ContactViewModel,
		});
	};
}

const getCancelConfirmationOptions = (
	destructiveOptionText: string,
	cancelText = 'Cancel'
): IConfirmationDialogOption<boolean>[] => {
	return [
		{
			isDestructive: true,
			representedObject: true,
			title: destructiveOptionText,
		},
		{
			isCancel: true,
			representedObject: false,
			title: cancelText,
		},
	];
};

interface IProps
	extends IModalContext,
		INotificationServiceComponentProps<Api.ICampaign[]>,
		IImpersonationContextComponentProps {
	approval?: CampaignsApprovalViewModel;
	campaign?: Api.CampaignViewModel;
	campaignGroup?: Api.CampaignViewModel[];
	className?: string;
	disableCampaignEdits?: boolean;
	disableContactEdits?: boolean;
	onContextFinishedEditingClicked?(e: React.MouseEvent<HTMLElement>): void;
	onScheduleCtaClicked?(e: React.MouseEvent<HTMLElement>, schedule: Api.IScheduledSend): void;
	readonlyContent?: boolean;
	requireEstimateToSend?: boolean;
	styles?: StyleDeclarationValue[];
}

export function _EditQueuedCampaign({
	postNotification,
	onContextFinishedEditingClicked: onContextFinishedEditingClickedProp,
	disableCampaignEdits,
	disableContactEdits,
	readonlyContent,
	onScheduleCtaClicked: onScheduleCtaClickedProp,
	requireEstimateToSend,
	parentModal,
	styles,
	className,
	campaignGroup,
	approval: approvalProp,
	impersonationContext: impersonationContextProp,
	campaign: campaignProp,
}: IProps) {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const match = useRouteMatch();
	const history = useHistory();
	const fullscreenModal = useFullscreenModal();
	const { logEvent, logApiError } = useEventLogging('EditQueuedCampaign');
	const [impersonationContext] = React.useState<Api.IImpersonationContext>(
		(impersonationContextProp?.isValid ? impersonationContextProp : null) || approvalProp?.impersonationContext
	);
	const [approval] = React.useState<CampaignsApprovalViewModel>(() => {
		if (approvalProp) {
			return approvalProp;
		}
		if (campaignProp?.status === Api.EmailSendStatus.WaitingForApproval) {
			return new CampaignsApprovalViewModel(userSession, [campaignProp]).impersonate(impersonationContext);
		}
		return null;
	});
	const [emailComposer, setEmailComposer] = React.useState<ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>>();
	const [composerContext, setComposerContext] =
		React.useState<IEmailComposerContext<ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>>>();
	const [isLoading, setIsLoading] = React.useState(true);
	const [showingCancelConfirmation, setShowingCancelConfirmation] = React.useState(false);
	const [showingCitations, setShowingCitations] = React.useState(false);
	const campaign = React.useMemo(
		() => approval?.activeCampaign || campaignGroup?.[0] || campaignProp,
		[approval?.activeCampaign, campaignGroup, campaignProp]
	);
	// An AI campaign's real bodies live per recipient. Its campaign-level body is a placeholder that is never sent, so
	// editing it or sending it as a test would only mislead; both are suppressed in favour of the review screen.
	const isAICampaign = !!campaign?.toJs()?.aiBrief;
	// The lead time only applies while the emails do not exist yet. Once generation has committed them, moving the
	// send closer is legitimate, and the server agrees: it keys its own check off the same field.
	const needsGenerationLeadTime = isAICampaign && !campaign?.toJs()?.aiBrief?.lastGeneratedDate;
	const earliestSend = earliestAICampaignSend();
	const cancelScheduleMutation = useEmailCampaignCancelScheduleMutation({
		onSuccess: _campaign => {
			campaign.setCampaign(_campaign);
			toast.success('Scheduled send cancelled');
			postNotification({
				info: [_campaign],
				topic: Topics.EDIT_CAMPAIGNS_ITEM,
			});
			invalidateAllInfiniteCampaignReportQueries();
			fullscreenModal?.dismissModal();
		},
		onError: error => {
			logApiError('CancelCampaign-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	// Rescheduling an AI campaign goes through the schedule-only endpoint rather than the whole-compose save. That
	// save posts the entire model back and its Subject is [Required], which an AI campaign has no reason to be
	// carrying in the composer, so a date change was rejected with "Subject must be provided". It also meant every
	// reschedule pushed the placeholder body back to the server, which is nothing anyone wants written.
	const updateScheduleMutation = useEmailUpdateSendScheduleMutation({
		onSuccess: _campaign => {
			campaign.setCampaign(_campaign);
			postNotification({
				info: [_campaign],
				topic: Topics.EDIT_CAMPAIGNS_ITEM,
			});
			invalidateAllInfiniteCampaignReportQueries();
			fullscreenModal?.history?.goBack();
		},
		onError: error => {
			logApiError('UpdateSchedule-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	React.useEffect(() => {
		async function load() {
			// The modal's close transition renders this route once more without its location state, mounting the
			// component with no campaign. There is nothing to load then, and trying would throw and push an error
			// dialog over a modal that is already closing.
			if (!campaign) {
				return;
			}

			try {
				logEvent('CampaignLoadInstanceForCampaign', { id: campaign.id });
				let _emailComposer: ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions> = null;
				if (approval) {
					await approval.campaignComposer.loadForEditing();
					_emailComposer = approval.campaignComposer as ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>;
				} else {
					_emailComposer = (await ComposeEmailViewModel.loadInstanceForCampaign(userSession, campaign)).impersonate(
						impersonationContext
					) as ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>;
				}
				if (_emailComposer) {
					setComposerContext({
						emailComposer: _emailComposer,
						sendTestEmail: isAICampaign ? undefined : createSendTestEmailHandler(_emailComposer),
					});
					setEmailComposer(_emailComposer);
					setIsLoading(false);
				}
			} catch (err) {
				logApiError('CampaignLoadInstanceForCampaign-Error', err);
				errorMessages.pushApiError(err);
			}
		}
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [approval, isAICampaign, logApiError, impersonationContext, userSession, errorMessages, logEvent]);
	const onComposerRequestClose = (_?: any, canceled?: boolean) => {
		if (canceled) {
			logEvent('Cancel');
		}
		parentModal?.onRequestClose(null, canceled);
	};
	const onNavigateTo = (path: string, locationState?: ILocationState<any, any>) => () => {
		history.push(`${match.url}${path}`, locationState);
	};
	const onDone = async (showNextComposer: boolean) => {
		if (approval?.campaignComposer && showNextComposer) {
			try {
				await approval?.campaignComposer.loadForEditing(approval.campaignComposer.campaign);
				await approval?.campaignComposer.loadDefaultMessageContent(true);
				setEmailComposer(approval.campaignComposer as ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>);
				onNavigateTo('', { viewModel: approval })();
			} catch (error) {
				logApiError(`CampaignApprovalLoadContent-Error`, error);
				errorMessages.pushApiError(error);
			}
		} else {
			fullscreenModal.dismissModal();
		}
	};
	const onApproved = () => {
		const locationState: ILocationState<CampaignsApprovalViewModel, any> = {
			viewModel: approval,
		};
		onNavigateTo('/approval/confirmation', locationState)();
	};
	const updateCampaign = async () => {
		emailComposer.editModeIsActive = false;

		try {
			await emailComposer?.updateCampaign();
			return true;
		} catch (error) {
			logApiError('UpdateCampaign-Error', error);
			errorMessages.pushApiError(error);
			return false;
		}
	};
	const onSaveAndReturnToSummaryClicked = async (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		try {
			const result = await updateCampaign();
			if (result) {
				invalidateAllInfiniteCampaignReportQueries();
				fullscreenModal?.history?.goBack();
			}
		} catch {
			// handled by update
		}
	};
	const onScheduleCtaClicked = (e: React.MouseEvent<HTMLElement>) => {
		onScheduleCtaClickedProp?.(e, emailComposer?.emailMessage?.options?.scheduledSend);
		if (e.isDefaultPrevented()) {
			return;
		}

		// An AI campaign's emails are written in the window before the send, so rescheduling one whose emails do not
		// exist yet needs to leave that window open, plus time to review what gets written. Once they are written, any
		// future time is fine, which is why this keys off lastGeneratedDate.
		if (needsGenerationLeadTime) {
			const startDate = emailComposer?.emailMessage?.options?.scheduledSend?.startDate;
			if (!startDate || new Date(startDate) < earliestSend) {
				toast.error(
					`Choose a time at least ${AI_CAMPAIGN_MINIMUM_LEAD_HOURS} hours out, which leaves time to write the emails and for you to review them.`
				);
				return;
			}
		}

		if (isAICampaign) {
			e.preventDefault();
			const scheduledSend = emailComposer?.emailMessage?.options?.scheduledSend;
			updateScheduleMutation.mutate({
				campaignId: campaign.id,
				impersonationContext,
				schedule: { criteria: Api.ScheduleCriteria.StartAfter, startDate: scheduledSend?.startDate },
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
			return;
		}

		onSaveAndReturnToSummaryClicked(e);
	};
	const onFinishComposing = (approved: boolean) => {
		if (approved) {
			onNavigateTo('/approval/set-schedule', fullscreenModal?.history?.location?.state)();
		} else {
			const locationState: ILocationState<CampaignsApprovalViewModel, any> = {
				viewModel: approval,
			};
			if (approval.pendingCampaigns.length === 0) {
				parentModal?.onRequestClose();
			} else {
				onNavigateTo('/approval/confirmation', locationState)();
			}
		}
	};
	const onContextFinishedEditingClicked = (e: React.MouseEvent<HTMLElement>) => {
		onContextFinishedEditingClickedProp?.(e);
		if (!e.isDefaultPrevented()) {
			if (emailComposer.isFollowUp) {
				e.preventDefault();
				fullscreenModal?.history?.goBack();
				return;
			}
			onSaveAndReturnToSummaryClicked(e);
		}
	};
	const showCancelConfirmation = () => {
		setShowingCancelConfirmation(true);
	};
	const approveCampaign = () => {
		const _campaign = approval.activeCampaign;
		if (_campaign && !approval.isBusy) {
			// approve also updates
			const promise = approval.approveCampaign();
			if (promise) {
				promise
					.then(() => {
						postNotification({
							info: [_campaign.toJs()],
							topic: Topics.EDIT_CAMPAIGNS_ITEM,
						});
						invalidateAllInfiniteCampaignReportQueries();
						onApproved();
					})
					.catch((error: Api.IOperationResultNoValue) => {
						logApiError(`Approve-Error`, error);
						errorMessages.pushApiError(error);
					});
				return promise;
			}
		}
		return null;
	};
	const onSendCompliance = (sendWithComplianceEmail?: string) => {
		const _campaign = approval?.activeCampaign;
		if (_campaign && !approval.isBusy) {
			// approve also updates
			approval
				.sendCompliance(sendWithComplianceEmail)
				.then(() => {
					postNotification({
						info: [_campaign.toJs()],
						topic: Topics.EDIT_CAMPAIGNS_ITEM,
					});
					invalidateAllInfiniteCampaignReportQueries();
					toast.success(`Copy sent to ${sendWithComplianceEmail || userSession?.user?.email}`);
				})
				.catch((error: Api.IOperationResultNoValue) => {
					logApiError(`Send-Error`, error);
					errorMessages.pushApiError(error);
				});
		}
	};
	const onApproveWithoutCompliance = () => {
		const nextEmailComposer = produce(emailComposer, draftEmailComposer => {
			draftEmailComposer.emailMessage.options = draftEmailComposer.emailMessage.options || {};
			draftEmailComposer.emailMessage.options.sendWithCompliance = false;
		});
		setEmailComposer(nextEmailComposer);
		const _campaign = approval?.activeCampaign;
		if (_campaign && !approval.isBusy) {
			approval
				.approveCampaign()
				.then(() => {
					postNotification({
						info: [_campaign.toJs()],
						topic: Topics.EDIT_CAMPAIGNS_ITEM,
					});
					invalidateAllInfiniteCampaignReportQueries();
					onApproved();
				})
				.catch((error: Api.IOperationResultNoValue) => {
					logApiError(`Approve-Error`, error);
					errorMessages.pushApiError(error);
				});
		}
	};
	const onRejectionConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!canceled && result?.representedObject) {
			const _campaign = approval.activeCampaign;
			const promise = approval.rejectCampaign();
			if (promise) {
				logEvent('RejectCampaign');
				promise
					.then(() => {
						toast.success('Scheduled send rejected');
						postNotification({
							info: [_campaign.toJs()],
							topic: Topics.EDIT_CAMPAIGNS_ITEM,
						});
						invalidateAllInfiniteCampaignReportQueries();
						onFinishComposing(false);
					})
					.catch((error: Api.IOperationResultNoValue) => {
						logApiError(`RejectCampaign-Error`);
						errorMessages.pushApiError(error);
					});
			}
		}
		setShowingCancelConfirmation(false);
	};
	const cancelCampaign = async () => {
		logEvent('CancelCampaign', { id: campaign?.id });
		cancelScheduleMutation.mutate({
			campaignId: campaign.id,
			impersonationContext: impersonationContextProp?.isValid
				? {
						account: impersonationContextProp?.account,
						// TODO: might need to impersonate with active admin, this user could have been deactivated.
						user: campaign?.creator,
					}
				: null,
		});
	};
	const onCancelConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (!canceled && result?.representedObject) {
			cancelCampaign();
		}
		setShowingCancelConfirmation(false);
	};
	const renderCancelConfirmationDialog = () => {
		const showForRejectAction = approval && !impersonationContextProp?.isValid;
		return (
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showingCancelConfirmation,
					onRequestClose: showForRejectAction ? onRejectionConfirmationRequestClose : onCancelConfirmationRequestClose,
				}}
				options={getCancelConfirmationOptions(showForRejectAction ? 'Reject' : 'Cancel Campaign', 'Nevermind')}
				title={`Are you sure you want to ${showForRejectAction ? 'reject' : 'cancel'} this email campaign?`}
			/>
		);
	};
	const onRenderViewCitations = () => {
		if (emailComposer.campaign.emailBodyTemplate?.citation == null) {
			return null;
		}
		return (
			<ViewCitationsModal
				modalProps={{
					isOpen: showingCitations,
					onRequestClose: () => setShowingCitations(false),
				}}
			>
				{emailComposer.campaign.emailBodyTemplate.citation}
			</ViewCitationsModal>
		);
	};
	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} edit-queued-campaign ${className || ''}`}>
			{!emailComposer?.isUserEditingMessage && (
				<MultiContainerHeader
					fullscreenHeader='View Campaign'
					// Opened straight from a report row there is nothing in-modal to go back to, and the arrow's
					// default (modal history back) would do nothing.
					hideFullscreenBackButton={!fullscreenModal?.history?.canGoBackWithinTrackedHistory}
					onFullscreenRequestClose={onComposerRequestClose}
				/>
			)}
			{isLoading ? null : (
				<EmailComposerContext.Provider value={composerContext}>
					<Switch>
						<PrivateRoute
							exact={true}
							path={`${match.url}/approval/confirmation`}
							render={props => (
								<BulkEmailApprovalConfirmation {...props} approval={approval} disableHeader={true} onDone={onDone} />
							)}
							userSession={userSession}
						/>
						<PrivateRoute
							exact={true}
							path={`${match.url}/approval/set-schedule`}
							render={props => (
								<BulkEmailApprovalSetSchedule
									{...props}
									approval={approval}
									disableHeader={true}
									onApproved={onApproved}
									onScheduleCtaClicked={onScheduleCtaClicked}
									requireEstimateToSend={requireEstimateToSend}
									scheduleCtaText='Okay'
								/>
							)}
							userSession={userSession}
						/>
						<PrivateRoute
							exact={true}
							path={`${match.url}/approval`}
							render={props => (
								<BulkEmailApprovalComposer {...props} approval={approval} onFinish={onFinishComposing} />
							)}
							userSession={userSession}
						/>
						<PrivateRoute
							exact={true}
							path={`${match.url}/send-options`}
							render={props => (
								<EditSendOptions
									{...props}
									campaign={campaign}
									disableSendNow={true}
									emailComposer={emailComposer}
									onScheduleCtaClicked={onScheduleCtaClicked}
									// An AI campaign's composer holds placeholder content the estimate call cannot price, so
									// requiring one leaves the confirm button silently dead.
									// Bounds the picker rather than only refusing on submit, so a date that cannot work is never
									// offered in the first place.
									minimumStartDate={needsGenerationLeadTime ? earliestSend : undefined}
									requireEstimateToSend={isAICampaign ? false : requireEstimateToSend}
									styles={[styleSheet.childRoute]}
									submitButtonText='Okay'
								/>
							)}
							userSession={userSession}
						/>
						<PrivateRoute
							exact={false}
							path={match.url}
							render={props => {
								const detailsSummary = props.location.pathname === match.url && (
									<CampaignDetailsSummary
										campaign={campaign}
										campaignGroup={campaignGroup}
										emailComposer={emailComposer}
										onApproveClicked={approveCampaign}
										onCancelClicked={showCancelConfirmation}
										onChangeScheduleClicked={() => {
											onNavigateTo(
												approval ? '/approval/set-schedule' : '/send-options',
												fullscreenModal?.history?.location?.state
											)();
										}}
										onSendCompliance={onSendCompliance}
										onApproveWithoutCompliance={onApproveWithoutCompliance}
										onEditRecipientsClicked={() => {
											onNavigateTo('/recipients', !approval ? fullscreenModal?.history?.location?.state : undefined)();
										}}
										onViewCitationsClicked={() => setShowingCitations(true)}
									/>
								);

								// An AI campaign has no campaign-level email to show or edit, so the composer view gives way to
								// a per-recipient preview: the selected recipient's real email on the left, the list on the right.
								if (isAICampaign) {
									return (
										<div className={css(styleSheet.childRoute)}>
											<AICampaignQueuedView
												campaignId={campaign.id}
												onCancelCampaign={showCancelConfirmation}
												onChangeSchedule={() => {
													onNavigateTo(
														approval ? '/approval/set-schedule' : '/send-options',
														fullscreenModal?.history?.location?.state
													)();
												}}
											/>
										</div>
									);
								}

								return (
									<EditCampaign
										contextFinishedEditingCtaText='Okay'
										disableCampaignEdits={disableCampaignEdits}
										disableContactEdits={disableContactEdits}
										emailComposer={
											(approval?.campaignComposer ||
												emailComposer) as ComposeEmailViewModel<Api.IEmailMessageFollowUpOptions>
										}
										initialEmailBodyTemplate={emailComposer.campaign?.emailBodyTemplate || undefined}
										isQueuedCampaign={true}
										onContentFinishedEditingClicked={updateCampaign}
										onContextFinishedEditingClicked={onContextFinishedEditingClicked}
										onRejectClicked={showCancelConfirmation}
										readonlyContent={readonlyContent}
										styles={[styleSheet.childRoute]}
									>
										{detailsSummary}
									</EditCampaign>
								);
							}}
							userSession={userSession}
						/>
						<Redirect to={`${match.url}`} push={false} />
					</Switch>
					{renderCancelConfirmationDialog()}
					{onRenderViewCitations()}
				</EmailComposerContext.Provider>
			)}
		</div>
	);
}

const EditQueuedCampaignAsObserver = observer(_EditQueuedCampaign);
const EditQueuedCampaignWithContext = inject(
	ModalChildComponentContextKey,
	ImpersonationContextKey
)(EditQueuedCampaignAsObserver);
export const EditQueuedCampaign = withNotificationService(EditQueuedCampaignWithContext);
